import React, { useEffect, useRef, useState } from 'react';
import { APIProvider, Map, Marker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { RxCross2 } from 'react-icons/rx';
import ModalHeading from '../../components/shared/modal-heading';
import Loader from '../../components/shared/loader';
import { locationIconSvg } from '../../svgs';

const locationIconDataUrl = 'data:image/svg+xml;base64,' + btoa(locationIconSvg);

const CustomPolyline = ({ path }) => {
    const map = useMap();

    useEffect(() => {
        if (!map || !path || path.length === 0) return;

        const polyline = new window.google.maps.Polyline({
            path: path,
            geodesic: true,
            strokeColor: '#47B2DB',
            strokeOpacity: 1.0,
            strokeWeight: 5,
        });

        polyline.setMap(map);

        return () => {
            polyline.setMap(null);
        };
    }, [map, path]);

    return null;
};

const PositionsMap = ({ positions, loading, error, onClose, attributes }) => {
    const mapRef = useRef(null);
    const mapInstance = useMap();
    const [selectedPosition, setSelectedPosition] = useState(null);
    // const [center, setCenter] = useState({ lat: 0, lng: 0 });

    useEffect(() => {
        if (!positions || positions.length === 0 || loading || error || !mapInstance) return;

        const bounds = new window.google.maps.LatLngBounds();
        positions.forEach((pos) => {
            bounds.extend({ lat: pos.latitude, lng: pos.longitude });
        });

        mapInstance.fitBounds(bounds, { padding: 50 });
    }, [positions, loading, error, mapInstance]);


    const handleMarkerClick = (pos) => {
        setSelectedPosition(pos);
    };

    const handleInfoWindowClose = () => {
        setSelectedPosition(null);
    };

    const path = positions?.map((pos) => ({ lat: pos.latitude, lng: pos.longitude })) || [];

    return (
        <div className="space-y-3 px-4 py-3 relative">
            <ModalHeading className="text-left mb-3">Positions Information</ModalHeading>

            <button
                className="hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full"
                onClick={onClose}
            >
                <RxCross2 className="text-xl" />
            </button>

            {loading && <Loader />}
            {error && !loading && (
                <div className="flex items-center justify-center h-[100px] text-red-600">
                    {error}
                </div>
            )}
            {!loading && !error && positions && positions.length > 0 && (
                <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                    <Map
                        ref={mapRef}
                        style={{ width: '100%', height: '500px' }}
                        defaultCenter={{ lat: positions[0]?.latitude || 0, lng: positions[0]?.longitude || 0 }}
                        defaultZoom={15}
                        mapTypeId="roadmap"
                        gestureHandling="greedy"
                        zoomControl={true}
                    >

                        {positions.map((pos) => (
                            <Marker
                                key={pos.positionId}
                                position={{ lat: pos.latitude, lng: pos.longitude }}
                                icon={{
                                    url: locationIconDataUrl,
                                    scaledSize: { width: 32, height: 32 },
                                    anchor: { x: 16, y: 32 },
                                    rotation: 90 - pos.course,
                                }}
                                onClick={() => handleMarkerClick(pos)}
                            />
                        ))}
                        <CustomPolyline path={path} />
                        {selectedPosition && (
                            <InfoWindow
                                position={{ lat: selectedPosition.latitude, lng: selectedPosition.longitude }}
                                options={{
                                    closeBoxURL: '', // Removes the close 'X'
                                    enableEventPropagation: true,
                                    pixelOffset: { width: 0, height: -30 }, // Move it up a bit
                                }}
                            >
                                <div className="pr-3 pb-3 space-y-1 text-sm">
                                    <p><strong>Speed:</strong> {Number(selectedPosition.speed).toFixed(3) + " " + attributes?.speedUnit}</p>
                                    <p><strong>Motion:</strong> {selectedPosition.motion}</p>
                                    <p><strong>Activity:</strong> {selectedPosition.activity || 'N/A'}</p>
                                </div>
                            </InfoWindow>
                        )}

                    </Map>
                </APIProvider>
            )}
        </div>
    );
};

export default PositionsMap;